import React, { useMemo } from 'react';
import { Card } from '../Card';
import { ReactComponent as QuestionnaireIcon } from 'assets/icons/questionnaire.svg';
import { useGetCombinedPatientDetailsQuery } from 'graphql/generated/remote-schema-hasura';
import {
  useGetLastChildQuestionnaireByPatientCodexId,
  useGetLastQuestionnaireByPatientCodexId,
} from '../../../my-patients/pages/patient/tabs/questionnaire/useGetLastQuestionnaireByPatientCodexId';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import Loader from 'components/loaderComponent';
import QuestionnaireBlock, {
  Block,
} from '../../../my-patients/pages/patient/tabs/questionnaire/QuestionnaireBlock';
import {
  CHILDREN_PROVIDER,
  HAVE_KIDNEY_STONES_QUESTION_ID,
  KIDNEY_STONES_QUESTION_ID,
  MED_ALLERGENS_QUESTION_ID,
} from 'components/dynamicQuestionnaire/utils/constants';
import KidneyStonesBlock, {
  KidneyStones,
} from '../../../my-patients/pages/patient/tabs/questionnaire/KidneyStonesBlock';
import { twMerge } from 'tailwind-merge';
import AllergensBlock, {
  Allergens,
} from '../../../my-patients/pages/patient/tabs/questionnaire/AlergensBlock';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../../firebase/interfaces';
import { ADDITIONAL_INFORMATION_ID } from '../../../my-account/pages/health-questionnaire/useQuestionaireValidations';
import AdditionalInfo from '../../../my-patients/pages/patient/tabs/questionnaire/AdditionalInfo';
import { ADDITIONAL_CHILD_INFORMATION_ID } from '../../../my-account/pages/my-profile/components/patientComponents/ChildHealthQuestionnaire/useChildQuestionaireValidations';

interface QuestionnaireProps {
  patientId?: string;
}

const Questionnaire: React.FC<QuestionnaireProps> = ({ patientId }) => {
  const { user } = useAuth();
  const { loading: localeLoading, data: locale } = useGetPage({
    pageId: pageIds.QUESTIONNAIRE_TAB_BY_MY_PATIENT_PAGE,
    locale: 'en',
  });

  const { data: patientData, loading: patientLoading } =
    useGetCombinedPatientDetailsQuery({
      variables: {
        patientCodexId: patientId ?? '',
        skipProviderDetails: user?.role === Roles.PATIENT,
      },
      errorPolicy: 'all',
    });

  const isChild = useMemo(
    () =>
      !!(
        patientData?.getFHIRPatientbyCodexID ||
        patientData?.getFHIRPatientbyCodexIDParameter
      )?.users?.[0]?.parentCodexId,
    [patientData],
  );

  const getData = isChild
    ? useGetLastChildQuestionnaireByPatientCodexId
    : useGetLastQuestionnaireByPatientCodexId;

  const { data, loading } = getData(patientId as string);
  const additionalInfo = (data?.[1] ?? []).filter(
    ({ questionID }) => +questionID === +ADDITIONAL_INFORMATION_ID,
  );
  const additionalChildInfo = (data?.[0] ?? []).filter(
    ({ questionID }) => +questionID === +ADDITIONAL_CHILD_INFORMATION_ID,
  );

  if (!locale || localeLoading) {
    return null;
  }

  if (patientLoading || loading) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col gap-2.5 w-full">
      <Card
        title={'Questionnaire'}
        titleClassName="text-h4 text-btn-black font-semibold"
        backgroundColor="bg-white"
        showSeparator
        Icon={QuestionnaireIcon}
      >
        <div
          className="flex flex-col w-full py-2.5 gap-30"
          data-testid="questionaire"
        >
          <div className="flex flex-col gap-30">
            <QuestionnaireBlock
              questionnaire={(data[0] ?? []).filter(({ questionID }) =>
                isChild ? CHILDREN_PROVIDER.includes(+questionID) : true,
              )}
              title={
                isChild
                  ? 'Who is your child’s primary doctor? Please include their full name and how we can contact them'
                  : locale.backgroundInfo
              }
            />

            <hr className={twMerge(!isChild && 'hidden')} />

            <div
              className={twMerge('flex flex-col gap-5', !isChild && 'hidden')}
            >
              {(data[0] ?? [])
                .filter(
                  ({ questionID }) => !CHILDREN_PROVIDER.includes(+questionID),
                )
                .map((questionnaire) => (
                  <Block key={questionnaire.questionID} {...questionnaire} />
                ))}
            </div>

            {isChild && (
              <AllergensBlock
                className="mt-0"
                questionnaire={
                  (data?.[0] ?? [])?.filter(
                    ({ questionID }) =>
                      +questionID === MED_ALLERGENS_QUESTION_ID,
                  ) as Allergens[]
                }
              />
            )}
          </div>
          <div
            className={twMerge(
              'pt-30 border-t border-t-gray-opacity-15',
              isChild && 'hidden',
            )}
          >
            <QuestionnaireBlock
              questionnaire={(data?.[1] ?? []).filter(
                ({ questionID }) =>
                  ![
                    KIDNEY_STONES_QUESTION_ID,
                    HAVE_KIDNEY_STONES_QUESTION_ID,
                    MED_ALLERGENS_QUESTION_ID,
                    +ADDITIONAL_INFORMATION_ID,
                  ].includes(+questionID),
              )}
              title={locale.healthHistory}
            />
            <AllergensBlock
              questionnaire={
                (data?.[1] ?? [])?.filter(
                  ({ questionID }) => +questionID === MED_ALLERGENS_QUESTION_ID,
                ) as Allergens[]
              }
            />
            {!isChild && (
              <>
                <QuestionnaireBlock
                  questionnaire={(data?.[1] ?? []).filter(
                    ({ questionID }) =>
                      +questionID === HAVE_KIDNEY_STONES_QUESTION_ID,
                  )}
                  title={' '}
                />

                <KidneyStonesBlock
                  questionnaire={
                    (data?.[1] ?? [])?.filter(
                      ({ questionID }) =>
                        +questionID === KIDNEY_STONES_QUESTION_ID,
                    ) as KidneyStones[]
                  }
                />
              </>
            )}
          </div>
          <div
            className={twMerge(
              'pt-30 border-t border-t-gray-opacity-15',
              isChild && 'hidden',
              !additionalInfo?.length && 'hidden',
            )}
          >
            <AdditionalInfo
              className="grid-cols-1"
              questionnaire={additionalInfo}
              title={'Additional information'}
              fileWrapperClassName="desktop:grid-cols-3 grid-cols-1"
              patientId={patientId}
            />
          </div>
          <div
            className={twMerge(
              'pt-30 border-t border-t-gray-opacity-15',
              !isChild && 'hidden',
              !additionalChildInfo?.length && 'hidden',
            )}
          >
            <AdditionalInfo
              className="grid-cols-1"
              questionnaire={additionalChildInfo}
              title={'Additional information'}
              fileWrapperClassName="desktop:grid-cols-3 grid-cols-1"
              patientId={patientId}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Questionnaire;
